import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type {
  GateInfo,
  GateListResponse,
  GateDecisionResponse,
} from "@/api/pipelineGates";
import { queryKeys } from "@/lib/queryKeys";

export type DecisionErrorKind = "" | "conflict" | "generic";

export interface UsePipelineGatesReturn {
  gates: GateInfo[];
  loading: boolean;
  error: string;
  approve: (phaseId: string) => void;
  reject: (phaseId: string, reason: string) => void;
  deciding: boolean;
  decisionError: DecisionErrorKind;
}

/**
 * Fetches gates for a pipeline run and provides approve/reject mutations.
 * Gates are fetched via `GET /api/v1/pipeline/runs/{runId}/gates`.
 * Mutations invalidate both the gates query and the broader `projects.all`
 * key so the run-list badge updates (D5 of design).
 *
 * D1: uses mutate() instead of mutateAsync() to avoid unhandled rejections.
 * D2: invalidation in onSettled so 409 errors still refetch gates.
 * D3: useEffect resets mutation state when runId changes.
 * D4: decisionError is a discriminated kind ("conflict" | "generic" | "").
 */
export function usePipelineGates(
  runId: string | null,
): UsePipelineGatesReturn {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.projects.pipelineGates(runId ?? ""),
    queryFn: () =>
      api<GateListResponse>(`/api/v1/pipeline/runs/${runId}/gates`),
    enabled: runId !== null,
  });

  const approveMutation = useMutation({
    mutationFn: (phaseId: string) =>
      api<GateDecisionResponse>(
        `/api/v1/pipeline/runs/${runId}/gates/${phaseId}/approve`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ phaseId, reason }: { phaseId: string; reason: string }) =>
      api<GateDecisionResponse>(
        `/api/v1/pipeline/runs/${runId}/gates/${phaseId}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ reason }),
        },
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.all,
      });
    },
  });

  // D3: reset mutation state when runId changes
  useEffect(() => {
    approveMutation.reset();
    rejectMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const mutationError = approveMutation.error ?? rejectMutation.error;
  const decisionError: DecisionErrorKind = !mutationError
    ? ""
    : mutationError instanceof ApiError && mutationError.status === 409
      ? "conflict"
      : "generic";

  return {
    gates: query.data?.gates ?? [],
    loading: runId !== null && query.isFetching,
    error: query.error ? "Failed to load gates" : "",
    approve: (phaseId: string) => {
      approveMutation.mutate(phaseId);
    },
    reject: (phaseId: string, reason: string) => {
      rejectMutation.mutate({ phaseId, reason });
    },
    deciding: approveMutation.isPending || rejectMutation.isPending,
    decisionError,
  };
}
